import { AnimatePresence, motion } from "framer-motion";
import { X, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useEffect } from "react";

const overlayTransition = { duration: 0.3 };
const imageTransition = { duration: 0.35, ease: [0.22, 1, 0.36, 1] };

export default function Lightbox({ images, index, onClose, onPrev, onNext, alt }) {
  useEffect(() => {
    if (index === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [index, onClose, onPrev, onNext]);

  const current = index !== null ? images[index] : null;
  const stop = (e) => e.stopPropagation();

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={overlayTransition}
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center"
          data-testid="gallery-lightbox"
          onClick={onClose}
        >
          <button
            onClick={onClose}
            data-testid="lightbox-close"
            aria-label="Close gallery"
            className="absolute top-6 right-6 md:top-8 md:right-8 z-10 p-3 border border-white/20 text-white hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
          >
            <X size={20} />
          </button>
          <button
            onClick={(e) => { stop(e); onPrev(); }}
            data-testid="lightbox-prev"
            aria-label="Previous image"
            className="absolute left-4 md:left-8 z-10 p-3 border border-white/20 text-white hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
          >
            <CaretLeft size={22} />
          </button>
          <button
            onClick={(e) => { stop(e); onNext(); }}
            data-testid="lightbox-next"
            aria-label="Next image"
            className="absolute right-4 md:right-8 z-10 p-3 border border-white/20 text-white hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
          >
            <CaretRight size={22} />
          </button>
          <motion.figure
            key={index}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={imageTransition}
            className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
            onClick={stop}
          >
            <img
              src={current.url}
              alt={current.alt || current.caption || alt}
              className="max-w-[90vw] max-h-[75vh] object-contain"
              data-testid="lightbox-image"
            />
            {current.caption && (
              <figcaption className="mt-5 text-white/70 text-sm md:text-base font-light text-center max-w-2xl px-4">
                {current.caption}
              </figcaption>
            )}
            <p className="mt-3 text-white/40 text-xs uppercase tracking-[0.22em]">
              {index + 1} / {images.length}
            </p>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
